import {
	FollowUpAgentStatusEnum,
	getQueryParamsString,
	OrderPartnerReviewRatingEnum,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { stringToBoolean } from "Infrastructure/Utilities/Helpers/convertStringToBool";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useMarketRegionList } from "Market/Api/Query/useMarketRegionList";
import { useMarket } from "Market/Providers/CurrentMarketProvider";
import { filteredUserIdAtom } from "Order/Atom/filteredUserIdAtom";
import { useB2B } from "Order/Components/Form/Hooks/useB2B";
import {
	getOrderFilterFormSchema,
	type OrderFilterFormModel,
} from "Order/Components/Form/Schema/orderFilterSchema";
import { OrderPaths } from "Order/Routes/Routing";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { DatePicker } from "Shared/Components/Form/CustomFormComponent/DatePicker";
import { FormAutocomplete } from "Shared/Components/Form/CustomFormComponent/FormAutocomplete";
import Checkbox from "Shared/Components/Form/CustomFormComponent/FormCheckboxBoolean";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { useManagerOptions } from "Shared/Hooks/useManagerOptions";
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_LIMIT,
} from "Shared/Hooks/useTableFilter";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2, Stack, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

type OrderFilterFormProps = {
	setIsFilterDrawerOpen: (isOpen: boolean) => void;
};

const { selectOptions, editForm, shared } = Resources.base.order;

const PENDING_STATUSES = [
	FollowUpAgentStatusEnum.in_progress,
	FollowUpAgentStatusEnum.agent_started,
];
const CALLBACK_STATUSES = [
	FollowUpAgentStatusEnum.not_completed_panic,
	FollowUpAgentStatusEnum.not_completed_timeout,
	FollowUpAgentStatusEnum.booking_abandoned,
	FollowUpAgentStatusEnum.agent_abandoned,
	FollowUpAgentStatusEnum.quote_deferred,
];
const COMPLETED_STATUSES = [
	FollowUpAgentStatusEnum.completed,
	FollowUpAgentStatusEnum.agent_completed,
	FollowUpAgentStatusEnum.booking_confirmed,
];

export function OrderFilterForm({
	setIsFilterDrawerOpen,
}: OrderFilterFormProps) {
	const { t } = useResource();
	const navigate = useNavigate();
	const currentMarket = useMarket().market;
	const [searchParams] = useSearchParams();
	const [_filteredUserId, setFilteredUserId] = useAtom(filteredUserIdAtom);
	const searchParameters = Object.fromEntries(searchParams.entries());
	const { b2bOptions } = useB2B(currentMarket);

	const { data: partners } = usePartnersListQuery({
		marketId: currentMarket?.id,
	});
	const { data: regions } = useMarketRegionList(currentMarket?.id);

	const managerOptions = useManagerOptions(currentMarket);

	const form = useForm<OrderFilterFormModel>({
		resolver: zodResolver(getOrderFilterFormSchema()),
		defaultValues: {
			...searchParameters,
			status: searchParams.getAll("status") as OrderStatus[],
			userId: searchParameters.userId ? Number(searchParameters.userId) : null,
			partnerId: searchParameters.partnerId
				? Number(searchParameters.partnerId)
				: null,
			regionId: searchParameters.regionId
				? Number(searchParameters.regionId)
				: null,
			unassignedPartner:
				stringToBoolean(searchParameters.unassignedPartner) ?? false,
			unassignedUser: stringToBoolean(searchParameters.unassignedUser) ?? false,
			notReviewed: stringToBoolean(searchParameters.notReviewed) ?? false,
			hasExamination: stringToBoolean(searchParameters.hasExamination) ?? false,
			b2b: searchParameters.b2b ?? null,
			promoCode: searchParameters.promoCode ?? null,
			followUpAgentStatus: (() => {
				const values = searchParams.getAll("followUpAgentStatus");
				const groups: string[] = [];
				if (
					values.some((v) =>
						PENDING_STATUSES.includes(v as FollowUpAgentStatusEnum),
					)
				) {
					groups.push("in_progress");
				}
				if (
					values.some((v) =>
						CALLBACK_STATUSES.includes(v as FollowUpAgentStatusEnum),
					)
				) {
					groups.push("not_completed");
				}
				if (
					values.some((v) =>
						COMPLETED_STATUSES.includes(v as FollowUpAgentStatusEnum),
					)
				) {
					groups.push("completed");
				}
				return groups;
			})(),
		},
	});

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = form;

	const handleSubmitFilter = () => {
		handleSubmit((data) => {
			if (data.followUpAgentStatus?.length) {
				data.followUpAgentStatus = data.followUpAgentStatus.flatMap((v) => {
					if (v === "in_progress") return PENDING_STATUSES;
					if (v === "not_completed") return CALLBACK_STATUSES;
					if (v === "completed") return COMPLETED_STATUSES;
					return [v];
				});
			}

			const filteredData = Object.fromEntries(
				Object.entries(data).filter(
					([_, value]) => value !== null && value !== undefined && value !== "",
				),
			);

			setFilteredUserId(null);
			navigate(
				`${OrderPaths.Index.path}${getQueryParamsString({ ...filteredData, pageLimit: DEFAULT_PAGE_LIMIT, pageIndex: DEFAULT_PAGE_INDEX })}`,
			);
			setIsFilterDrawerOpen(false);
		})();
	};

	const handleResetFilter = () => {
		form.reset({
			status: [],
			dateNextActionFrom: "",
			dateNextActionTo: "",
			dateStartingFrom: "",
			dateStartingTo: "",
			regionId: null,
			partnerId: null,
			unassignedPartner: false,
			userId: null,
			unassignedUser: false,
			query: "",
			review: null,
			notReviewed: false,
			hasExamination: false,
			b2b: null,
			promoCode: null,
			followUpAgentStatus: [],
		});
		navigate(
			`${OrderPaths.Index.path}${getQueryParamsString({ pageLimit: DEFAULT_PAGE_LIMIT, pageIndex: DEFAULT_PAGE_INDEX })}`,
		);
	};

	useEffect(() => {
		const subscription = form.watch((value, { name }) => {
			if (name === "unassignedPartner" && value.unassignedPartner === true) {
				form.setValue("partnerId", null);
			}
			if (name === "unassignedUser" && value.unassignedUser === true) {
				form.setValue("userId", null);
			}
			if (name === "notReviewed" && value.notReviewed === true) {
				form.setValue("review", null);
			}
		});
		return () => subscription.unsubscribe();
	}, [form]);

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="status"
						name="status"
						label={t(editForm.status)}
						variant="outlined"
						control={control}
						errors={errors}
						options={createSelectOptions(
							OrderStatus,
							t,
							selectOptions.orderStatus,
						)}
						fullWidth
						multiple
						sxLabel={{ mt: 0 }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Checkbox
							id="has-examination"
							name="hasExamination"
							control={control}
							errors={errors}
							sxLabel={{ mt: 0 }}
							sx={{ pr: 1 }}
						/>
						<Typography variant="caption">
							{t(editForm.hasExamination)}
						</Typography>
					</Stack>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="followUpAgentStatus"
						name="followUpAgentStatus"
						label="Agent Status"
						variant="outlined"
						control={control}
						errors={errors}
						options={[
							{ value: "in_progress", label: "Pending" },
							{ value: "not_completed", label: "Callback requests" },
							{ value: "completed", label: "Completed" },
						]}
						multiple
						fullWidth
						sxLabel={{ mt: 0 }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6 }}>
					<DatePicker
						name="dateNextActionFrom"
						label={t(editForm.dateNextActionFrom)}
						control={control}
						errors={errors}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6 }}>
					<DatePicker
						name="dateNextActionTo"
						label={t(editForm.dateNextActionTo)}
						control={control}
						errors={errors}
					/>
				</Grid2>

				<Grid2 size={{ xs: 6 }}>
					<DatePicker
						name="dateStartingFrom"
						label={t(editForm.dateStartingFrom)}
						control={control}
						errors={errors}
					/>
				</Grid2>
				<Grid2 size={{ xs: 6 }}>
					<DatePicker
						name="dateStartingTo"
						label={t(editForm.dateStartingTo)}
						control={control}
						errors={errors}
					/>
				</Grid2>
				{currentMarket?.id && (
					<Grid2 size={{ xs: 12 }}>
						<Select
							id="region-id"
							name="regionId"
							label={t(editForm.marketRegion)}
							variant="outlined"
							control={control}
							errors={errors}
							options={(regions || []).map((region) => ({
								value: region.id,
								label: region.name,
							}))}
							fullWidth
							numeric
							sxLabel={{ mt: 0 }}
						/>
					</Grid2>
				)}

				{b2bOptions.length > 0 && (
					<Grid2 size={{ xs: 12 }}>
						<Select
							id="b2b"
							name="b2b"
							label={t(editForm.b2b)}
							variant="outlined"
							control={control}
							errors={errors}
							options={b2bOptions}
							fullWidth
							sxLabel={{ mt: 0 }}
						/>
					</Grid2>
				)}

				<Grid2 size={{ xs: 12 }}>
					<Input
						id="promoCode"
						name="promoCode"
						label={t(editForm.promoCode)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						sxLabel={{ mt: 0 }}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormAutocomplete
						id="partner-id"
						name="partnerId"
						label={t(editForm.partner)}
						control={control}
						errors={errors}
						options={(partners?.data || []).map(({ partner }) => ({
							value: partner.id,
							label:
								`${partner.name} ${partner.middleName ? `${partner.middleName} ` : ""}${partner.surname}`.trim(),
						}))}
						fullWidth
						numeric
						sxLabel={{ mt: 0 }}
						disabled={form.watch("unassignedPartner") === true}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Checkbox
							id="unassigned-partner"
							name="unassignedPartner"
							control={control}
							errors={errors}
							sx={{ pr: 1 }}
						/>
						<Typography variant="caption">
							{t(editForm.unassignedPartner)}
						</Typography>
					</Stack>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<FormAutocomplete
						id="user-id"
						name="userId"
						label={t(editForm.user)}
						control={control}
						errors={errors}
						options={managerOptions}
						fullWidth
						numeric
						sxLabel={{ mt: 0 }}
						groupBy={(option) => option.group as string}
						disabled={form.watch("unassignedUser") === true}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Checkbox
							id="unassigned-user"
							name="unassignedUser"
							control={control}
							errors={errors}
							sxLabel={{ mt: 0 }}
							sx={{ pr: 1 }}
						/>
						<Typography variant="caption">
							{t(editForm.unassignedUser)}
						</Typography>
					</Stack>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Select
						id="review"
						name="review"
						label={t(editForm.review)}
						variant="outlined"
						control={control}
						errors={errors}
						options={createSelectOptions(
							OrderPartnerReviewRatingEnum,
							t,
							selectOptions.orderPartnerReviewRating,
						)}
						fullWidth
						sxLabel={{ mt: 0 }}
						disabled={form.watch("notReviewed") === true}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Checkbox
							id="not-reviewed"
							name="notReviewed"
							control={control}
							errors={errors}
							sx={{ pr: 1 }}
						/>
						<Typography variant="caption">{t(editForm.notReviewed)}</Typography>
					</Stack>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Input
						id="query"
						name="query"
						label={t(shared.query)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						sxLabel={{ mt: 0 }}
					/>
				</Grid2>
			</Grid2>
			<Stack mt={2} direction="row" spacing={2}>
				<Box>
					<Button variant="contained" onClick={handleSubmitFilter}>
						{t(shared.search)}
					</Button>
				</Box>
				<Box>
					<Button variant="outlined" onClick={handleResetFilter}>
						{t(shared.reset)}
					</Button>
				</Box>
			</Stack>
		</>
	);
}
